/**
 * Overlay BODIES — TASK 7.2 Step 3.
 *
 * Four of the five overlays of the mock's shell, extracted verbatim. Each
 * factory returns an {@link OverlayDescriptor} for ./OverlayStack.js, which
 * owns order, dismissal and focus; these functions own markup only.
 *
 * The fifth — the FULL PLAYER (mock L730-768) — moved to
 * `../views/player/FullPlayer.js` in TASK 8.3: it stopped being static markup
 * the moment it had to repaint from the transport on every tick, and it now
 * owns a `SeekBar` and a §10.6 banner as well.
 *
 *   ActionSheet      mock L771-783  (`sheet.open`)   — also the MORE menu
 *   ConfirmModal     mock L785-796  (`cm.open`)
 *   InputModal       mock L798-811  (`im.open`)
 *   DeleteDataModal  mock L813-826  (`ddm.open`)
 *
 * Static styling comes from the mapped classes in ../styles/README.md; the
 * handful of `{{ … }}` bindings the mock interpolated into `style=`
 * (`it.col`, `cm.confirmBg`, `ddm.btnBg`/`btnOpacity`, `fp.bg`,
 * `fp.shufCol`/`repeatCol`/`favCol`/`bannerCol`) are applied through
 * `styleProps` (CSSOM), never a style string.
 *
 * The mock's `onClick="{{ … }}"` props are all `addEventListener` here (W3).
 *
 * @module
 */

import { append, el } from '../components/primitives/dom.js';

import { controlTestId } from './testids.js';

/** @typedef {import('./OverlayStack.js').OverlayDescriptor} OverlayDescriptor */

/**
 * @typedef {object} SheetItem
 * @property {string} icon glyph shown in the 22px leading slot
 * @property {string} label
 * @property {string} [color] CSS colour for the row (mock `it.col`)
 * @property {() => void} onSelect
 */

/**
 * Bottom action sheet — mock L771-783. Used for album/track menus and, with a
 * fixed item list, for the primary-nav MORE menu (./MoreMenu.js).
 *
 * @param {{
 *   title: string,
 *   items: SheetItem[],
 *   onDismissRequest: () => void,
 *   testIdPrefix?: string,
 * }} options
 *   `onDismissRequest` is called for a backdrop tap; the stack — not this
 *   component — decides what dismissal means.
 * @returns {OverlayDescriptor}
 */
export function ActionSheet(options) {
  const { title, items, onDismissRequest } = options;

  // mock L772 — the scrim IS the dialog node in the mock; kept verbatim.
  const scrim = el('div', {
    className: 'sm-sheet-scrim',
    attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-label': title },
    on: {
      click: (event) => {
        // Backdrop tap only — a tap that landed on the panel bubbles up here.
        if (event.target === scrim) onDismissRequest();
      },
    },
  });

  const panel = el('div', { className: 'sm-sheet sm-anim-up-fast' }); // mock L773
  const list = el('div', { className: 'sm-sheet__list' }); // mock L776

  for (const item of items) {
    // mock L778
    const button = el('button', {
      className: 'sm-sheet__item',
      testId: controlTestId(item.label),
      attrs: { type: 'button' },
      ...(item.color === undefined ? {} : { styleProps: { color: item.color } }),
      on: { click: () => item.onSelect() },
    });
    append(
      button,
      el('span', {
        className: 'sm-sheet__item-icon',
        text: item.icon,
        attrs: { 'aria-hidden': 'true' },
      }),
      document.createTextNode(item.label),
    );
    list.append(button);
  }

  append(
    panel,
    el('div', { className: 'sm-sheet__handle', attrs: { 'aria-hidden': 'true' } }), // mock L774
    el('div', { className: 'sm-sheet__title', text: title }), // mock L775
    list,
  );
  scrim.append(panel);

  return { kind: 'sheet', element: scrim, dialog: scrim };
}

/**
 * Confirm modal — mock L785-796.
 *
 * @param {{
 *   title: string,
 *   body: string,
 *   confirmLabel: string,
 *   confirmBackground?: string,
 *   onConfirm: () => void,
 *   onCancel: () => void,
 * }} options
 * @returns {OverlayDescriptor}
 */
export function ConfirmModal(options) {
  const {
    title,
    body,
    confirmLabel,
    confirmBackground = 'var(--sm-color-pink)',
    onConfirm,
    onCancel,
  } = options;

  const scrim = el('div', {
    className: 'sm-modal-scrim',
    attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-label': title },
  });
  const panel = el('div', { className: 'sm-modal sm-anim-up-fast' });

  const cancel = el('button', {
    className: 'sm-btn sm-btn--ghost',
    text: 'Cancel',
    testId: controlTestId('Cancel'),
    attrs: { type: 'button' },
    on: { click: () => onCancel() },
  });
  const confirm = el('button', {
    className: 'sm-btn sm-btn--modal-confirm',
    text: confirmLabel,
    testId: controlTestId(confirmLabel),
    attrs: { type: 'button' },
    styleProps: { background: confirmBackground }, // mock `{{ cm.confirmBg }}`
    on: { click: () => onConfirm() },
  });

  append(
    panel,
    el('div', { className: 'sm-modal__title', text: title }),
    el('p', { className: 'sm-body sm-body--card', text: body }),
    append(el('div', { className: 'sm-actions sm-actions--end' }), cancel, confirm),
  );
  scrim.append(panel);

  return { kind: 'confirm', element: scrim, dialog: scrim, initialFocus: cancel };
}

/**
 * Single-field input modal — mock L798-811 (new playlist / rename / save
 * queue). `<form>` so Enter submits, exactly as the mock's `onSubmit` did.
 *
 * @param {{
 *   title: string,
 *   label: string,
 *   value?: string,
 *   placeholder?: string,
 *   confirmLabel?: string,
 *   onSubmit: (value: string) => string|null,
 *   onCancel: () => void,
 * }} options
 *   `onSubmit` returns an error message to display, or null on success.
 * @returns {OverlayDescriptor}
 */
export function InputModal(options) {
  const {
    title,
    label,
    value = '',
    placeholder = '',
    confirmLabel = 'Save',
    onSubmit,
    onCancel,
  } = options;

  const scrim = el('div', {
    className: 'sm-modal-scrim',
    attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-label': title },
  });

  const input = el('input', {
    className: 'sm-input sm-input--block',
    attrs: {
      id: 'sm-im',
      type: 'text',
      value,
      placeholder,
      maxlength: '100',
      'aria-describedby': 'sm-im-error',
    },
  });
  /** @type {HTMLInputElement} */ (input).value = value;

  // role=alert so a validation failure is announced (mock L804).
  const error = el('p', {
    className: 'sm-form-error sm-form-error--modal',
    attrs: { id: 'sm-im-error', role: 'alert', hidden: true },
  });

  const form = el('form', {
    className: 'sm-modal sm-modal--input sm-anim-up-fast',
    on: {
      submit: (event) => {
        event.preventDefault();
        const message = onSubmit(/** @type {HTMLInputElement} */ (input).value);
        if (message === null) return;
        error.textContent = message;
        error.removeAttribute('hidden');
        input.focus({ preventScroll: true });
      },
    },
  });

  append(
    form,
    el('div', { className: 'sm-modal__title sm-modal__title--teal', text: title }),
    el('label', { className: 'sm-form-label', text: label, attrs: { for: 'sm-im' } }),
    input,
    error,
    append(
      el('div', { className: 'sm-actions sm-actions--end sm-actions--end-top' }),
      el('button', {
        className: 'sm-btn sm-btn--ghost',
        text: 'Cancel',
        testId: controlTestId('Cancel'),
        attrs: { type: 'button' },
        on: { click: () => onCancel() },
      }),
      el('button', {
        className: 'sm-btn sm-btn--modal-confirm',
        text: confirmLabel,
        testId: controlTestId(confirmLabel),
        attrs: { type: 'submit' },
        styleProps: { background: 'var(--sm-color-teal)' }, // mock L807
      }),
    ),
  );
  scrim.append(form);

  return { kind: 'input', element: scrim, dialog: scrim, initialFocus: input };
}

/**
 * Irreversible-purge modal — mock L813-826. The confirm button stays disabled
 * until the typed value matches the target email or the literal word DELETE
 * (mock `_ddm()` L1149).
 *
 * @param {{ email: string, onSubmit: () => void, onCancel: () => void }} options
 * @returns {OverlayDescriptor}
 */
export function DeleteDataModal(options) {
  const { email, onSubmit, onCancel } = options;

  const scrim = el('div', {
    className: 'sm-modal-scrim sm-modal-scrim--danger',
    attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Delete user data' },
  });

  const input = el('input', {
    className: 'sm-input sm-input--block',
    attrs: { id: 'sm-ddm', type: 'text', placeholder: email },
  });

  const confirm = el('button', {
    className: 'sm-btn sm-btn--modal-confirm',
    text: 'Purge data',
    testId: controlTestId('Purge data'),
    attrs: { type: 'submit', disabled: true },
    // mock `{{ ddm.btnBg }}` / `{{ ddm.btnOpacity }}`
    styleProps: { background: 'var(--sm-color-text-dim-2)', opacity: '.5' },
  });

  /** mock L1149 — `match` */
  const matches = () => {
    const typed = /** @type {HTMLInputElement} */ (input).value.trim();
    return typed === email || typed === 'DELETE';
  };
  input.addEventListener('input', () => {
    const ok = matches();
    if (ok) confirm.removeAttribute('disabled');
    else confirm.setAttribute('disabled', '');
    confirm.style.setProperty(
      'background',
      ok ? 'var(--sm-color-pink)' : 'var(--sm-color-text-dim-2)',
    );
    confirm.style.setProperty('opacity', ok ? '1' : '.5');
  });

  const form = el('form', {
    className: 'sm-modal sm-modal--danger sm-anim-up-fast',
    on: {
      submit: (event) => {
        event.preventDefault();
        if (!matches()) return;
        onSubmit();
      },
    },
  });

  // mock L817 — the email is emphasised inside the body copy.
  const bodyCopy = el('p', { className: 'sm-body sm-body--modal' });
  append(
    bodyCopy,
    document.createTextNode('This wipes all personal data for '),
    el('strong', { className: 'sm-strong', text: email }),
    document.createTextNode(
      ' — favorites, playlists, queue, history, downloads, stats. It cannot be undone.',
    ),
  );

  // mock L818
  const warnLabel = el('label', {
    className: 'sm-form-label sm-form-label--warn',
    attrs: { for: 'sm-ddm' },
  });
  append(
    warnLabel,
    document.createTextNode('Type '),
    el('strong', { text: email }),
    document.createTextNode(' or the word DELETE to confirm'),
  );

  append(
    form,
    el('div', {
      className: 'sm-modal__title sm-modal__title--danger',
      text: 'IRREVERSIBLE PURGE',
    }),
    bodyCopy,
    warnLabel,
    input,
    append(
      el('div', { className: 'sm-actions sm-actions--end sm-actions--end-top' }),
      el('button', {
        className: 'sm-btn sm-btn--ghost',
        text: 'Cancel',
        testId: controlTestId('Cancel'),
        attrs: { type: 'button' },
        on: { click: () => onCancel() },
      }),
      confirm,
    ),
  );
  scrim.append(form);

  return { kind: 'deleteData', element: scrim, dialog: scrim, initialFocus: input };
}
